import {createSelector, createSlice} from '@reduxjs/toolkit'
import {
  OfferClickedActionType,
  TRACK_LOCKED_OFFER_CLICKED,
  TRACK_OFFER_CLICKED
} from 'middleware/analytics/actions/trackOfferClicked'
import {RootState} from 'store'

export interface PriceAlertPromptState {
  isOpen: boolean
  /**
   * The hotelId of the AP on which the user last clicked into an offer (View
   * rooms / View deal). The proactive prompt is suppressed for this hotel only —
   * engaging with Hotel A never suppresses Hotel B. `null` = no engagement yet.
   */
  engagedOfferHotelId: string | null
}

const initialState: PriceAlertPromptState = {
  isOpen: false,
  engagedOfferHotelId: null
}

const isOfferClickedAction = (action: {
  type: string
}): action is OfferClickedActionType =>
  action.type === TRACK_OFFER_CLICKED ||
  action.type === TRACK_LOCKED_OFFER_CLICKED

const {actions, reducer} = createSlice({
  name: 'priceAlertPrompt',
  initialState,
  reducers: {
    showPriceAlertPrompt: state => {
      state.isOpen = true
    },
    hidePriceAlertPrompt: state => {
      state.isOpen = false
    }
  },
  extraReducers: builder => {
    // Record the hotel the user engaged an offer on. Offer clicks can only target
    // the hotel whose AP is currently open, so `params.hotelId` is the current AP.
    builder.addMatcher(isOfferClickedAction, (state, action) => {
      state.engagedOfferHotelId = action.params.hotelId
    })
  }
})

const getPriceAlertPromptState = (state: RootState) => state.priceAlertPrompt

export const getPriceAlertPromptVisibility = createSelector(
  [getPriceAlertPromptState],
  priceAlertPrompt => Boolean(priceAlertPrompt?.isOpen)
)

export const getEngagedOfferHotelId = createSelector(
  [getPriceAlertPromptState],
  priceAlertPrompt => priceAlertPrompt?.engagedOfferHotelId ?? null
)

export const {showPriceAlertPrompt, hidePriceAlertPrompt} = actions

export default reducer
